// src/screens/AddEvent2.jsx
// 🆕 VERSION CHATBOT — design chat, thème sombre (bg-color4 / texte blanc).
// - Validations regex strictes par champ + data-testid pour tests auto.
// - Description via RichTextEditor (ReactQuill), identique à AddEvent.jsx.
// - Chaque réponse a un crayon ✏️ : la réponse est modifiée SUR PLACE,
//   puis on revient à la question en cours (pas de re-questionnement).

import React, { useEffect, useRef, useState } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import { addEvent, getCategories } from "../services/CreateurService";
import RichTextEditor from "../components/RichTextEditor";
import { getStatutStripe, demarrerOnboardingStripe } from "../services/StripeConnectService";
import { peutAgirPour, getCreateurActif, getTokenPublication } from "../utils/agirPour";

const MIN_IMAGES = 1;
const SECONDARY_IMAGES = 4;
const DELAI_FRAPPE = 1800; // ms — durée de l'indicateur "en train d'écrire"

// Sous-étapes qui dépendent d'une réponse parente
const PARENT = {
    tarif_titre: "gratuit",
    tarif_prix: "gratuit",
    tarif_more: "gratuit",
    billetterie_lien: "billetterie_type",
    adresse: "typeEvent",
    lien_reunion: "typeEvent",
};

// ── Numérotation des questions / réponses pour les tests auto ──
// Question bot   → data-testid="Q{n}"
// Réponse user   → data-testid="R{n}"
// Input          → data-testid="input-R{n}"
// Bouton envoi   → data-testid="btn-envoyer-R{n}"
const NUM = {
    img_main: 1, img_secondary: 2, category: 3, nom: 4, sousnom: 5, date: 6,
    duree: 7, gratuit: 8, tarif_titre: 9, tarif_prix: 10, tarif_more: 11,
    typeEvent: 12, adresse: 13, lien_reunion: 14, access_restaurant: 15,
    access_parking: 16, access_auditive: 17, access_handicap: 18,
    billetterie_type: 19, billetterie_lien: 20, description: 21, confirmation: 22,
};
const qid = (step) => (NUM[step] ? `Q${NUM[step]}` : "Q0");
const rid = (step) => (NUM[step] ? `R${NUM[step]}` : "R0");

const heureActuelle = () =>
    new Date().toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });

// Retire les balises HTML (pour compter/valider le texte de la description)
const stripHtml = (html) => (html || "").replace(/<[^>]*>/g, "").trim();

// ══════════════════════════════════════════════════════════
//  RÈGLES DE VALIDATION PAR CHAMP
// ══════════════════════════════════════════════════════════

const REGLES = {
    nom: {
        regex: /^(?=.*[A-Za-zÀ-ÿ0-9]).{3,100}$/,
        maxLength: 100,
        message: "Le nom doit contenir entre 3 et 100 caractères.",
    },
    sousnom: {
        regex: /^[A-Za-zÀ-ÿ'-]+(\s[A-Za-zÀ-ÿ'-]+)?$/,
        maxLength: 40,
        message: "Le sous-titre doit contenir 1 ou 2 mots maximum (lettres uniquement).",
    },
    duree: {
        regex: /^(?=.*[0-9])[0-9A-Za-zÀ-ÿ\s]{1,30}$/,
        maxLength: 30,
        message: "Indique une durée avec au moins un chiffre (ex : 2h30, 1 jour, 45 min).",
    },
    cp: {
        regex: /^\d{5}$/,
        maxLength: 5,
        message: "Le code postal doit contenir exactement 5 chiffres.",
    },
    ville: {
        regex: /^[A-Za-zÀ-ÿ\s'-]{2,100}$/,
        maxLength: 100,
        message: "La ville ne doit contenir que des lettres (2 à 100 caractères).",
    },
    rue: {
        regex: /^[A-Za-zÀ-ÿ0-9\s'.,-]{5,150}$/,
        maxLength: 150,
        message: "L'adresse doit contenir entre 5 et 150 caractères valides.",
    },
    billetterie_lien: {
        regex: /^https?:\/\/[^\s]+\.[^\s]{2,}$/i,
        maxLength: 500,
        message: "Le lien doit être une URL valide (ex : https://exemple.fr).",
    },
    lien_reunion: {
        regex: /^https?:\/\/[^\s]+\.[^\s]{2,}$/i,
        maxLength: 500,
        message: "Le lien doit être une URL valide (ex : https://meet.google.com/xxx).",
    },
    tarif_titre: {
        regex: /^[A-Za-zÀ-ÿ0-9\s'-]{2,50}$/,
        maxLength: 50,
        message: "Le titre du tarif doit contenir entre 2 et 50 caractères.",
    },
    description: {
        regex: /^(?=.*[A-Za-zÀ-ÿ]).{20,2000}$/s,
        maxLength: 2000,
        message: "La description doit contenir entre 20 et 2000 caractères.",
    },
};

// ══════════════════════════════════════════════════════════
//  AVATAR BOT
// ══════════════════════════════════════════════════════════

const AvatarBot = () => (
    <div
        className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold shrink-0"
        style={{ backgroundColor: "#481699" }}
        data-testid="avatar-bot"
    >
        🎪
    </div>
);

// ══════════════════════════════════════════════════════════
//  BULLE DE MESSAGE — avec crayon d'édition pour les réponses user
// ══════════════════════════════════════════════════════════

const Bulle = ({ from, text, heure, editable, onEdit, testid }) => {
    const estBot = from === "bot";

    if (estBot) {
        return (
            <div className="flex items-end gap-2 mb-3 justify-start" data-testid="message-bot">
                <AvatarBot />
                <div className="flex flex-col items-start max-w-[75%] min-w-0">
                    <div className="px-4 py-2.5 text-sm leading-relaxed bg-white/10 text-white rounded-2xl rounded-bl-md border border-white/10 break-words [overflow-wrap:anywhere] max-w-full" data-testid={testid}>
                        {text}
                    </div>
                    <span className="text-[10px] text-white/30 mt-1 px-1">{heure}</span>
                </div>
            </div>
        );
    }

    return (
        <div className="flex items-end gap-2 mb-3 justify-end" data-testid="message-user">
            {editable && (
                <button
                    onClick={onEdit}
                    title="Modifier cette réponse"
                    data-testid={`btn-modifier-${testid}`}
                    className="mb-1 w-6 h-6 flex items-center justify-center rounded-full text-white/40 hover:text-white hover:bg-white/10 transition text-xs shrink-0"
                >
                    ✏️
                </button>
            )}
            <div className="flex flex-col items-end max-w-[75%] min-w-0">
                <div
                    data-testid={testid}
                    className="px-4 py-2.5 text-sm leading-relaxed text-white rounded-2xl rounded-br-md break-words [overflow-wrap:anywhere] max-w-full"
                    style={{ backgroundColor: "#481699" }}
                >
                    {text}
                </div>
                <span className="text-[10px] text-white/30 mt-1 px-1">{heure}</span>
            </div>
        </div>
    );
};

// ══════════════════════════════════════════════════════════
//  INDICATEUR "EN TRAIN D'ÉCRIRE"
// ══════════════════════════════════════════════════════════

const EnTrainDecrire = () => (
    <div className="flex items-end gap-2 mb-3" data-testid="indicateur-frappe">
        <AvatarBot />
        <div className="bg-white/10 border border-white/10 rounded-2xl rounded-bl-md px-4 py-3">
            <div className="flex gap-1">
                {[0, 1, 2].map((i) => (
                    <span
                        key={i}
                        className="w-1.5 h-1.5 rounded-full bg-white/50 animate-bounce"
                        style={{ animationDelay: `${i * 0.15}s` }}
                    />
                ))}
            </div>
        </div>
    </div>
);

// ══════════════════════════════════════════════════════════
//  COMPOSANT PRINCIPAL
// ══════════════════════════════════════════════════════════

const AddEvent2 = () => {
    const navigate = useNavigate();
    // Consultant / admin dans l'espace /consultant → publie pour le créateur choisi
    const agitPour = peutAgirPour();
    const createurActif = agitPour ? getCreateurActif() : null;
    const pageRetour = agitPour ? "/consultant" : "/EspaceClient";
    const scrollRef = useRef(null);
    const envoiEnCoursRef = useRef(false); // verrou synchrone anti double-clic
    const editionRef = useRef(null); // état synchrone de l'édition en cours
    const questionCouranteRef = useRef(null); // dernière question posée (hors édition)

    const [messages, setMessages] = useState([
        createurActif
            ? { from: "bot", text: `Salut 👋 Tu publies pour ${createurActif.nom}. Je t'aide à créer l'événement, étape par étape.`, heure: heureActuelle(), step: "img_main" }
            : { from: "bot", text: "Salut 👋 Je vais t'aider à créer ton événement, étape par étape.", heure: heureActuelle(), step: "img_main" },
    ]);
    const [botEcrit, setBotEcrit] = useState(false);
    const [edition, setEdition] = useState(null);

    const [step, setStep] = useState("img_main");
    const [isSubmitting, setIsSubmitting] = useState(false);

    // ── Catégories ─────────────────────────────────────────
    const [categories, setCategories] = useState([]);
    const [loadingCategories, setLoadingCategories] = useState(false);
    const [erreurCategories, setErreurCategories] = useState(false);

    const chargerCategories = () => {
        setLoadingCategories(true);
        setErreurCategories(false);
        getCategories()
            .then((cats) => {
                setCategories(cats || []);
            })
            .catch((err) => {
                console.error("Erreur chargement catégories :", err);
                setErreurCategories(true);
            })
            .finally(() => {
                setLoadingCategories(false);
            });
    };

    useEffect(() => {
        if (step === "category" && categories.length === 0 && !loadingCategories && !erreurCategories) {
            chargerCategories();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [step]);

    // ── Données du formulaire ──────────────────────────────
    const [images, setImages] = useState(Array(MIN_IMAGES + SECONDARY_IMAGES).fill(null));
    const [previewUrls, setPreviewUrls] = useState(Array(MIN_IMAGES + SECONDARY_IMAGES).fill(null));

    const [selectedCategory, setSelectedCategory] = useState(null);
    const [nomEven, setNomEven] = useState("");
    const [sousNomEven, setSousNomEven] = useState("");
    const [dateTimeEven, setDateTimeEven] = useState("");
    const [duree, setDuree] = useState("");
    const [isGratuit, setIsGratuit] = useState(null);

    const [tarifs, setTarifs] = useState([]);
    const [tarifTitreTmp, setTarifTitreTmp] = useState("");

    const [typeEvent, setTypeEvent] = useState(null); // "Présentiel" | "En Ligne"
    const [lienReunion, setLienReunion] = useState("");

    const [codePostalEven, setCodePostalEven] = useState("");
    const [villeEven, setVilleEven] = useState("");
    const [rueEven, setRueEven] = useState("");

    // ── Autocomplétion adresse (api-adresse.data.gouv.fr) ──
    const [adresseQuery, setAdresseQuery] = useState("");
    const [suggestionsAdresse, setSuggestionsAdresse] = useState([]);
    const [rechercheAdresseEnCours, setRechercheAdresseEnCours] = useState(false);
    const [adresseManuelle, setAdresseManuelle] = useState(false);
    const [adresseChoisie, setAdresseChoisie] = useState(null); // libellé complet affiché

    const [isRestaurant, setIsRestaurant] = useState(null);
    const [isParking, setIsParking] = useState(null);
    const [isAuditive, setIsAuditive] = useState(null);
    const [isHandicap, setIsHandicap] = useState(null);

    const [billetterieType, setBilletterieType] = useState(null);
    const [lienBilletterie, setLienBilletterie] = useState("");
    const [prixBillet, setPrixBillet] = useState("");
    const [nbrPlacesMax, setNbrPlacesMax] = useState("");

    const [description, setDescription] = useState("");
    const [saisie, setSaisie] = useState("");

    // ── Statut Stripe Connect du créateur ──────────────────
    const [stripeStatut, setStripeStatut] = useState(null); // "actif" | "non_connecte" | ...
    useEffect(() => {
        // Consultant / admin : pas d'accès au Stripe du créateur (route bloquée)
        if (agitPour) { setStripeStatut("non_concerne"); return; }
        getStatutStripe()
            .then((s) => setStripeStatut(s.statut))
            .catch(() => setStripeStatut("non_connecte"));
    }, []);
    const stripeActif = stripeStatut === "actif";

    // ── Scroll auto vers le bas ────────────────────────────
    useEffect(() => {
        scrollRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages, step, botEcrit]);

    // ── Recherche d'adresse (API officielle gouvernementale) ──
    // https://api-adresse.data.gouv.fr — France uniquement, gratuite, sans clé.
    useEffect(() => {
        if (step !== "adresse" || adresseManuelle) return;
        if (adresseQuery.trim().length < 3) {
            setSuggestionsAdresse([]);
            return;
        }
        const controller = new AbortController();
        const timer = setTimeout(() => {
            setRechercheAdresseEnCours(true);
            fetch(
                `https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(adresseQuery.trim())}&limit=10&autocomplete=1`,
                { signal: controller.signal }
            )
                .then((res) => res.json())
                .then((data) => {
                    setSuggestionsAdresse(data?.features || []);
                })
                .catch((err) => {
                    if (err.name !== "AbortError") console.error("Erreur API adresse :", err);
                })
                .finally(() => setRechercheAdresseEnCours(false));
        }, 350);

        return () => {
            clearTimeout(timer);
            controller.abort();
        };
    }, [adresseQuery, step, adresseManuelle]);

    // ── Ajoute (ou remplace en mode édition) un message user ──
    const direUser = (content, options = {}) => {
        const msg = {
            from: "user",
            text: content,
            heure: heureActuelle(),
            editStep: options.editStep ?? null,
            editValue: options.editValue,
            parent: PARENT[step],
            step,
        };

        const ed = editionRef.current;
        if (ed && !ed.remplace) {
            ed.remplace = true;
            // Remplace la réponse sur place + retire l'ancienne sous-branche
            // (ex : anciens tarifs si on modifie "gratuit")
            setMessages((prev) =>
                prev
                    .map((m, i) => (i === ed.index ? { ...msg, heure: m.heure, parent: m.parent, step: m.step } : m))
                    .filter((m) => m.parent !== ed.editStep)
            );
            return;
        }
        setMessages((prev) => [...prev, msg]);
    };

    // ── Le bot "réfléchit" puis répond ──────────────────────
    const direBot = (content, delai = DELAI_FRAPPE, extra = {}) => {
        setBotEcrit(true);
        setTimeout(() => {
            setBotEcrit(false);
            setMessages((prev) => [...prev, { from: "bot", text: content, heure: heureActuelle(), ...extra }]);
        }, delai);
    };

    // ── Fin d'édition : retour à la question où on était ────
    const finirEdition = (nextStep, question) => {
        const ed = editionRef.current;
        editionRef.current = null;
        setEdition(null);

        // Si on était dans la sous-branche de la réponse modifiée,
        // on continue le flux normal
        if (PARENT[ed.retour] === ed.editStep) {
            allerA(nextStep, question);
            return;
        }

        setStep(ed.retour);
        setSaisie(ed.saisie);
        // Si des questions ont été ajoutées entre-temps, on repose la question en cours
        if (ed.branche && ed.question) direBot(ed.question, DELAI_FRAPPE, { step: ed.retour });
    };

    const allerA = (nextStep, question) => {
        const ed = editionRef.current;

        if (ed && !PARENT[nextStep]) {
            finirEdition(nextStep, question);
            return;
        }
        if (ed) ed.branche = true; // ex : gratuit → payant : il faut saisir les tarifs

        setStep(nextStep);
        if (question) {
            if (!ed) questionCouranteRef.current = question;
            direBot(question, DELAI_FRAPPE, { parent: PARENT[nextStep], step: nextStep });
        }
    };

    // ── Modifier une réponse précédente (crayon ✏️) ─────────
    const modifierReponse = (index) => {
        const msg = messages[index];
        if (!msg || !msg.editStep || editionRef.current || isSubmitting) return;

        const ed = {
            index,
            editStep: msg.editStep,
            retour: step,
            saisie,
            question: questionCouranteRef.current,
            remplace: false,
            branche: false,
        };
        editionRef.current = ed;
        setEdition(ed);

        if (msg.editStep === "date") {
            setDateTimeEven(msg.editValue ?? "");
        } else {
            setSaisie(typeof msg.editValue === "string" ? msg.editValue : "");
        }

        setStep(msg.editStep);
    };

    const annulerEdition = () => {
        const ed = editionRef.current;
        if (!ed) return;
        editionRef.current = null;
        setEdition(null);
        setStep(ed.retour);
        setSaisie(ed.saisie);
    };

    // ── Validation générique par regex ──────────────────────
    const valider = (regleId, valeur) => {
        const regle = REGLES[regleId];
        if (!regle) return true;
        if (!valeur || !valeur.trim()) {
            toast.error("Ce champ est obligatoire.");
            return false;
        }
        if (valeur.length > regle.maxLength) {
            toast.error(`Maximum ${regle.maxLength} caractères autorisés.`);
            return false;
        }
        if (!regle.regex.test(valeur.trim())) {
            toast.error(regle.message);
            return false;
        }
        return true;
    };

    // ── Helpers images ──────────────────────────────────────

    const TAILLE_MAX_IMAGE = 2 * 1024 * 1024; // 2 Mo

    const handleImageFile = (file, index) => {
        if (!file) return;

        if (file.size > TAILLE_MAX_IMAGE) {
            const tailleMo = (file.size / (1024 * 1024)).toFixed(1);
            toast.error(`Image trop lourde (${tailleMo} Mo). Maximum autorisé : 2 Mo.`);
            return;
        }

        const url = URL.createObjectURL(file);
        const newPreview = [...previewUrls];
        const newImages = [...images];
        newPreview[index] = url;
        newImages[index] = file;
        setPreviewUrls(newPreview);
        setImages(newImages);
    };

    // ── Tarifs ───────────────────────────────────────────────
    // Pas de crayon sur les tarifs : on les modifie via le ✏️ de "Non, c'est payant".

    const validerTarifTitre = () => {
        if (!valider("tarif_titre", saisie)) return;
        const titre = saisie.trim();
        setTarifTitreTmp(titre);
        direUser(titre);
        setSaisie("");
        allerA("tarif_prix", "Et le prix pour ce tarif (en €) ?");
    };

    const validerTarifPrix = () => {
        if (!saisie.trim()) { toast.error("Le prix est obligatoire."); return; }
        const val = parseFloat(saisie.replace(",", "."));
        if (isNaN(val)) { toast.error("Entre un prix numérique valide."); return; }
        if (val < 0) { toast.error("Le prix ne peut pas être négatif."); return; }
        if (val > 9999) { toast.error("Le prix ne peut pas dépasser 9999 €."); return; }

        const nouveauTarif = { titre: tarifTitreTmp, prix: val.toFixed(2) };
        setTarifs((prev) => [...prev, nouveauTarif]);
        direUser(`${val.toFixed(2)} €`);
        setSaisie("");
        setTarifTitreTmp("");
        allerA("tarif_more", "Veux-tu ajouter un autre tarif ?");
    };

    // ── Soumission finale ────────────────────────────────────

    const envoyerEvenement = async () => {
        if (envoiEnCoursRef.current) return;

        const imageTropLourde = images.find(
            (img) => img && img.size > TAILLE_MAX_IMAGE
        );
        if (imageTropLourde) {
            const tailleMo = (imageTropLourde.size / (1024 * 1024)).toFixed(1);
            toast.error(`Une image dépasse 2 Mo (${tailleMo} Mo). Remplace-la avant d'envoyer.`);
            return;
        }

        envoiEnCoursRef.current = true;
        setIsSubmitting(true);
        try {
            const token = getTokenPublication();
            const formData = new FormData();

            const prixEvenJson = isGratuit ? "0" : (tarifs.length ? JSON.stringify(tarifs) : "");

            formData.append("categorieID", selectedCategory.id);
            formData.append("nomEven", nomEven);
            formData.append("sousNomEven", sousNomEven);
            formData.append("dateTimeEven", dateTimeEven);
            formData.append("duree", duree);
            formData.append("prixEven", prixEvenJson);

            formData.append("typeEvent", typeEvent || "Présentiel");
            if (typeEvent === "En Ligne") {
                formData.append("lien_reunion", lienReunion);
            } else {
                formData.append("codePostalEven", codePostalEven);
                formData.append("villeEven", villeEven);
                formData.append("rueEven", rueEven);
            }
            formData.append("description", description);

            formData.append("site", billetterieType === "externe" ? lienBilletterie : "");

            formData.append("isGratuit", isGratuit ? 1 : 0);
            formData.append("isRestaurant", isRestaurant ? 1 : 0);
            formData.append("isParking", isParking ? 1 : 0);
            formData.append("isAuditive", isAuditive ? 1 : 0);
            formData.append("isHandicap", isHandicap ? 1 : 0);

            formData.append("billetterie_type", billetterieType);
            if (billetterieType === "externe" && lienBilletterie.trim()) {
                formData.append("lien_billetterie", lienBilletterie);
            }
            if (billetterieType === "thrundrz") {
                if (prixBillet !== "") formData.append("prix_billet", prixBillet);
                if (nbrPlacesMax !== "") formData.append("nbr_places_max", nbrPlacesMax);
            }

            images.forEach((img) => { if (img) formData.append("images[]", img); });

            await addEvent(formData, token);
            toast.success("Événement ajouté !");

            // Billetterie Thrundrz sans paiements activés → on envoie vers Stripe
            // (jamais pour un consultant / admin : c'est au créateur d'activer Stripe)
            if (billetterieType === "thrundrz" && !stripeActif && !agitPour) {
                direBot("🎉 Événement créé ! Je t'emmène sur Stripe pour activer tes paiements…", 300);
                setTimeout(() => {
                    demarrerOnboardingStripe("/EspaceClient/billetterie").catch(() => navigate("/EspaceClient/billetterie"));
                }, 1800);
                return;
            }

            direBot("🎉 Événement créé avec succès ! Redirection...", 300);
            setTimeout(() => navigate(pageRetour), 1800);
        } catch (err) {
            console.error(err);
            direBot("😕 Une erreur est survenue. Réessaie.", 300);
            toast.error("Erreur lors de l'ajout de l'événement.");
        } finally {
            setIsSubmitting(false);
            envoiEnCoursRef.current = false;
        }
    };

    const formatDateTime = (iso) => {
        if (!iso) return "";
        return new Date(iso).toLocaleDateString("fr-FR", {
            day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit",
        }).replace(",", " à");
    };

    // ══════════════════════════════════════════════════════
    //  RENDU DU WIDGET SELON L'ÉTAPE COURANTE
    // ══════════════════════════════════════════════════════

    const renderWidget = () => {
        switch (step) {

            // ── IMAGE PRINCIPALE ──────────────────────────
            case "img_main":
                return (
                    <div className="flex flex-col gap-3">
                        {previewUrls[0] ? (
                            <div className="relative">
                                <img src={previewUrls[0]} alt="aperçu" className="h-36 w-full object-cover rounded-xl" data-testid="preview-image-principale" />
                                <label className="absolute bottom-2 right-2 bg-black/60 text-white text-xs px-3 py-1.5 rounded-lg cursor-pointer hover:bg-black/80 transition">
                                    Changer
                                    <input type="file" accept="image/*" className="hidden" data-testid="input-R1"
                                        onChange={(e) => handleImageFile(e.target.files[0], 0)} />
                                </label>
                            </div>
                        ) : (
                            <label className="border-2 border-dashed border-white/20 rounded-xl p-6 text-center cursor-pointer hover:border-color1 transition">
                                <input type="file" accept="image/*" className="hidden" data-testid="input-R1"
                                    onChange={(e) => handleImageFile(e.target.files[0], 0)} />
                                <span className="text-white/40 text-sm">📷 Cliquer pour choisir une image</span>
                            </label>
                        )}
                        <button
                            disabled={!images[0]}
                            data-testid="btn-envoyer-R1"
                            onClick={() => {
                                direUser("✅ Image ajoutée", { editStep: "img_main" });
                                allerA("img_secondary", "Veux-tu ajouter des images supplémentaires ? (optionnel — jusqu'à 4)");
                            }}
                            className={`w-full py-2.5 rounded-xl font-semibold transition ${images[0]
                                ? "text-white cursor-pointer"
                                : "bg-white/10 text-white/30 cursor-not-allowed"
                                }`}
                            style={images[0] ? { backgroundColor: "#481699" } : {}}
                        >
                            Continuer →
                        </button>
                    </div>
                );

            // ── IMAGES SECONDAIRES ────────────────────────
            case "img_secondary":
                return (
                    <div className="flex flex-col gap-3">
                        <div className="flex gap-2 flex-wrap">
                            {[1, 2, 3, 4].map((i) => (
                                <label key={i} className="w-16 h-16 rounded-lg border-2 border-dashed border-white/20 flex items-center justify-center cursor-pointer overflow-hidden hover:border-color1 transition">
                                    {previewUrls[i] ? (
                                        <img src={previewUrls[i]} alt="" className="w-full h-full object-cover" data-testid={`preview-image-secondaire-${i}`} />
                                    ) : (
                                        <span className="text-white/20 text-xl">+</span>
                                    )}
                                    <input type="file" accept="image/*" className="hidden" data-testid={`input-R2-${i}`}
                                        onChange={(e) => handleImageFile(e.target.files[0], i)} />
                                </label>
                            ))}
                        </div>
                        <button
                            data-testid="btn-envoyer-R2"
                            onClick={() => {
                                direUser("C'est bon pour les images 👍", { editStep: "img_secondary" });
                                allerA("category", "Dans quelle catégorie classer cet événement ?");
                            }}
                            className="w-full py-2.5 rounded-xl text-white font-semibold transition"
                            style={{ backgroundColor: "#481699" }}
                        >
                            Continuer →
                        </button>
                    </div>
                );

            // ── CATÉGORIE ──────────────────────────────────
            case "category":
                return (
                    <div className="flex flex-col gap-2" data-testid="input-R3">
                        {loadingCategories ? (
                            <p className="text-white/40 text-sm text-center py-4">Chargement des catégories…</p>
                        ) : erreurCategories ? (
                            <div className="text-center py-4">
                                <p className="text-red-400 text-sm mb-3">
                                    Impossible de charger les catégories.
                                </p>
                                <button
                                    onClick={chargerCategories}
                                    data-testid="btn-reessayer-categories"
                                    className="px-4 py-2 rounded-xl text-white font-semibold text-sm"
                                    style={{ backgroundColor: "#481699" }}
                                >
                                    Réessayer
                                </button>
                            </div>
                        ) : categories.length === 0 ? (
                            <p className="text-white/40 text-sm text-center py-4">Aucune catégorie disponible.</p>
                        ) : (
                            <div className="grid grid-cols-2 gap-2 max-h-64 overflow-y-auto pr-1">
                                {categories.map((cat) => (
                                    <button
                                        key={cat.id}
                                        data-testid={`btn-envoyer-R3-${cat.id}`}
                                        onClick={() => {
                                            setSelectedCategory(cat);
                                            const nom = cat.nomCat || cat.nom || cat.name;
                                            direUser(nom, { editStep: "category" });
                                            allerA("nom", "Quel est le nom de l'événement ?");
                                        }}
                                        className="text-left px-3 py-2.5 rounded-xl border border-white/15 hover:border-color1 hover:bg-white/10 transition text-sm font-medium text-white"
                                    >
                                        {cat.nomCat || cat.nom || cat.name}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                );

            // ── CHAMPS TEXTE AVEC VALIDATION REGEX ─────────
            case "nom":
            case "sousnom":
            case "duree":
            case "billetterie_lien":
            case "lien_reunion": {
                const config = {
                    nom: { placeholder: "Ex : Concert Jazz", next: "sousnom", question: "Quel est le sous-titre de l'événement ? (2 mots maximum)" },
                    sousnom: { placeholder: "Ex : Soirée jazz", next: "date", question: "Quelle est la date et l'heure de l'événement ?" },
                    duree: { placeholder: "Ex : 2h30", next: "gratuit", question: "Cet événement est-il gratuit ?" },
                    billetterie_lien: { placeholder: "https://...", next: "description", question: "Décris l'événement (20 caractères min).", type: "url" },
                    lien_reunion: { placeholder: "https://meet.google.com/xxx", next: "billetterie_type", question: "Comment les gens vont-ils réserver ? Lien externe ou billetterie Thrundrz ?", type: "url" },
                }[step];

                const regle = REGLES[step];

                const validerChamp = () => {
                    if (!valider(step, saisie)) return;
                    const setters = {
                        nom: setNomEven, sousnom: setSousNomEven, duree: setDuree,
                        billetterie_lien: setLienBilletterie,
                        lien_reunion: setLienReunion,
                    };
                    const valeur = saisie.trim();
                    setters[step](valeur);
                    direUser(valeur, { editStep: step, editValue: valeur });
                    setSaisie("");
                    allerA(config.next, config.question);
                };

                return (
                    <div className="flex flex-col gap-1">
                        <div className="flex gap-2">
                            <input
                                type={config.type || "text"}
                                autoFocus
                                maxLength={regle?.maxLength}
                                value={saisie}
                                onChange={(e) => setSaisie(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && validerChamp()}
                                placeholder={config.placeholder}
                                data-testid={`input-R${NUM[step]}`}
                                className="flex-1 bg-white/10 border border-white/15 text-white placeholder-white/30 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-color1"
                            />
                            <button onClick={validerChamp} data-testid={`btn-envoyer-R${NUM[step]}`} className="px-5 py-2.5 rounded-xl text-white font-semibold text-sm" style={{ backgroundColor: "#481699" }}>
                                Envoyer
                            </button>
                        </div>
                        {regle?.maxLength && (
                            <span className="text-[11px] text-white/30 px-1">{saisie.length}/{regle.maxLength}</span>
                        )}
                    </div>
                );
            }

            // ── DATE ────────────────────────────────────────
            case "date":
                return (
                    <div className="flex gap-2">
                        <input
                            type="datetime-local"
                            autoFocus
                            value={dateTimeEven}
                            data-testid="input-R6"
                            onChange={(e) => setDateTimeEven(e.target.value)}
                            className="flex-1 bg-white/10 border border-white/15 text-white rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-color1 [color-scheme:dark]"
                        />
                        <button
                            data-testid="btn-envoyer-R6"
                            onClick={() => {
                                if (!dateTimeEven) { toast.error("La date est obligatoire."); return; }
                                if (new Date(dateTimeEven) < new Date()) {
                                    toast.error("La date doit être dans le futur.");
                                    return;
                                }
                                direUser(formatDateTime(dateTimeEven), { editStep: "date", editValue: dateTimeEven });
                                allerA("duree", "Quelle est la durée de l'événement ? (ex : 2h, 2h30, 1 jour)");
                            }}
                            className="px-5 py-2.5 rounded-xl text-white font-semibold text-sm"
                            style={{ backgroundColor: "#481699" }}
                        >
                            Envoyer
                        </button>
                    </div>
                );

            // ── GRATUIT ? ────────────────────────────────────
            case "gratuit":
                return (
                    <div className="flex gap-3">
                        <button
                            data-testid="btn-envoyer-R8-oui"
                            onClick={() => {
                                setIsGratuit(true);
                                setTarifs([]);
                                direUser("Oui, c'est gratuit 🎉", { editStep: "gratuit" });
                                allerA("typeEvent", "Ton événement est-il en présentiel ou en ligne ?");
                            }}
                            className="flex-1 py-2.5 rounded-xl bg-green-600 text-white font-semibold text-sm hover:bg-green-700 transition"
                        >
                            Oui, gratuit
                        </button>
                        <button
                            data-testid="btn-envoyer-R8-non"
                            onClick={() => {
                                setIsGratuit(false);
                                setTarifs([]);
                                direUser("Non, c'est payant", { editStep: "gratuit" });
                                allerA("tarif_titre", "Ajoutons un premier tarif. Quel est son titre ? (ex : Adulte, Étudiant)");
                            }}
                            className="flex-1 py-2.5 rounded-xl bg-gray-500 text-white font-semibold text-sm hover:bg-gray-600 transition"
                        >
                            Non, payant
                        </button>
                    </div>
                );

            // ── TARIF : TITRE ────────────────────────────────
            case "tarif_titre":
                return (
                    <div className="flex flex-col gap-1">
                        <div className="flex gap-2">
                            <input
                                autoFocus
                                maxLength={REGLES.tarif_titre.maxLength}
                                value={saisie}
                                onChange={(e) => setSaisie(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && validerTarifTitre()}
                                placeholder="Ex : Adulte"
                                data-testid="input-R9"
                                className="flex-1 bg-white/10 border border-white/15 text-white placeholder-white/30 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-color1"
                            />
                            <button onClick={validerTarifTitre} data-testid="btn-envoyer-R9" className="px-5 py-2.5 rounded-xl text-white font-semibold text-sm" style={{ backgroundColor: "#481699" }}>
                                Envoyer
                            </button>
                        </div>
                        <span className="text-[11px] text-white/30 px-1">{saisie.length}/{REGLES.tarif_titre.maxLength}</span>
                    </div>
                );

            // ── TARIF : PRIX ─────────────────────────────────
            case "tarif_prix":
                return (
                    <div className="flex gap-2">
                        <input
                            autoFocus type="number" step="0.01" min="0" max="9999"
                            value={saisie}
                            onChange={(e) => setSaisie(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && validerTarifPrix()}
                            placeholder="Ex : 20.00"
                            data-testid="input-R10"
                            className="flex-1 bg-white/10 border border-white/15 text-white placeholder-white/30 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-color1"
                        />
                        <button onClick={validerTarifPrix} data-testid="btn-envoyer-R10" className="px-5 py-2.5 rounded-xl text-white font-semibold text-sm" style={{ backgroundColor: "#481699" }}>
                            Envoyer
                        </button>
                    </div>
                );

            // ── TARIF : UN AUTRE ? ───────────────────────────
            case "tarif_more":
                return (
                    <div className="flex gap-3">
                        <button
                            data-testid="btn-envoyer-R11-oui"
                            onClick={() => { direUser("Oui, un autre tarif"); allerA("tarif_titre", "Titre du tarif suivant ?"); }}
                            className="flex-1 py-2.5 rounded-xl bg-white/10 text-white font-semibold text-sm hover:bg-white/15 transition border border-white/15"
                        >
                            + Ajouter un tarif
                        </button>
                        <button
                            data-testid="btn-envoyer-R11-non"
                            onClick={() => {
                                if (tarifs.length === 0) { toast.error("Ajoute au moins un tarif."); return; }
                                direUser("C'est tout pour les tarifs");
                                allerA("typeEvent", "Ton événement est-il en présentiel ou en ligne ?");
                            }}
                            className="flex-1 py-2.5 rounded-xl text-white font-semibold text-sm"
                            style={{ backgroundColor: "#481699" }}
                        >
                            Continuer →
                        </button>
                    </div>
                );

            // ── TYPE D'ÉVÉNEMENT : PRÉSENTIEL OU EN LIGNE ────
            case "typeEvent":
                return (
                    <div className="flex gap-3">
                        <button
                            data-testid="btn-envoyer-R12-presentiel"
                            onClick={() => {
                                setTypeEvent("Présentiel");
                                setLienReunion("");
                                direUser("Présentiel 📍", { editStep: "typeEvent" });
                                allerA("adresse", "Où se déroule l'événement ? Tape l'adresse et choisis-la dans la liste.");
                            }}
                            className="flex-1 py-2.5 rounded-xl text-white font-semibold text-sm"
                            style={{ backgroundColor: "#481699" }}
                        >
                            📍 Présentiel
                        </button>
                        <button
                            data-testid="btn-envoyer-R12-enligne"
                            onClick={() => {
                                setTypeEvent("En Ligne");
                                setCodePostalEven("");
                                setVilleEven("");
                                setRueEven("");
                                direUser("En ligne 💻", { editStep: "typeEvent" });
                                allerA("lien_reunion", "Quel est le lien de connexion (Zoom, Google Meet, Teams...) ?");
                            }}
                            className="flex-1 py-2.5 rounded-xl bg-gray-500 text-white font-semibold text-sm hover:bg-gray-600 transition"
                        >
                            💻 En ligne
                        </button>
                    </div>
                );

            // ── ADRESSE — autocomplétion API gouvernementale ──
            //    (api-adresse.data.gouv.fr — France uniquement, gratuite)
            case "adresse": {
                const validerSuggestion = (feature) => {
                    const p = feature.properties || {};
                    setCodePostalEven(p.postcode || "");
                    setVilleEven(p.city || "");
                    setRueEven(p.name || p.label || "");
                    setAdresseChoisie(p.label);
                    direUser(`📍 ${p.label}`, { editStep: "adresse", editValue: p.label });
                    setAdresseQuery("");
                    setSuggestionsAdresse([]);
                    allerA("access_restaurant", "Y a-t-il un restaurant sur place ?");
                };

                const validerManuel = () => {
                    if (!valider("cp", codePostalEven)) return;
                    if (!valider("ville", villeEven)) return;
                    if (!valider("rue", rueEven)) return;
                    const label = `${rueEven}, ${codePostalEven} ${villeEven}`;
                    direUser(`📍 ${label}`, { editStep: "adresse", editValue: label });
                    allerA("access_restaurant", "Y a-t-il un restaurant sur place ?");
                };

                return (
                    <div className="flex flex-col gap-2">
                        {!adresseManuelle ? (
                            <>
                                {/* ── Recherche d'adresse (autocomplétion en direct, nationale) ── */}
                                <input
                                    type="text"
                                    autoFocus
                                    value={adresseQuery}
                                    onChange={(e) => setAdresseQuery(e.target.value)}
                                    placeholder="Ex : 12 rue de la Liberté, Dijon"
                                    data-testid="input-R13"
                                    className="bg-white/10 border border-white/15 text-white placeholder-white/30 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-color1"
                                />
                                {rechercheAdresseEnCours && (
                                    <span className="text-[11px] text-white/40 px-1">Recherche…</span>
                                )}
                                {suggestionsAdresse.length > 0 && (
                                    <div className="flex flex-col gap-1 max-h-48 overflow-y-auto" data-testid="liste-suggestions-adresse">
                                        {suggestionsAdresse.map((f, i) => (
                                            <button
                                                key={i}
                                                data-testid={`btn-envoyer-R13-suggestion-${i}`}
                                                onClick={() => validerSuggestion(f)}
                                                className="text-left px-3 py-2 rounded-lg border border-white/15 hover:border-color1 hover:bg-white/10 transition text-sm text-white"
                                            >
                                                {f.properties?.label}
                                            </button>
                                        ))}
                                    </div>
                                )}
                                <button
                                    onClick={() => setAdresseManuelle(true)}
                                    data-testid="btn-adresse-manuelle"
                                    className="text-xs text-white/50 underline hover:text-white self-start"
                                >
                                    Je préfère saisir l'adresse moi-même
                                </button>
                            </>
                        ) : (
                            <>
                                <input
                                    type="text" maxLength={5} value={codePostalEven}
                                    onChange={(e) => setCodePostalEven(e.target.value)}
                                    placeholder="Code postal (ex : 21000)"
                                    data-testid="input-R13-cp"
                                    className="bg-white/10 border border-white/15 text-white placeholder-white/30 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-color1"
                                />
                                <input
                                    type="text" value={villeEven}
                                    onChange={(e) => setVilleEven(e.target.value)}
                                    placeholder="Ville (ex : Dijon)"
                                    data-testid="input-R13-ville"
                                    className="bg-white/10 border border-white/15 text-white placeholder-white/30 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-color1"
                                />
                                <input
                                    type="text" value={rueEven}
                                    onChange={(e) => setRueEven(e.target.value)}
                                    placeholder="Rue (ex : 12 rue de la Liberté)"
                                    data-testid="input-R13-rue"
                                    className="bg-white/10 border border-white/15 text-white placeholder-white/30 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-color1"
                                />
                                <button
                                    onClick={validerManuel}
                                    data-testid="btn-envoyer-R13"
                                    className="w-full py-2.5 rounded-xl text-white font-semibold text-sm"
                                    style={{ backgroundColor: "#481699" }}
                                >
                                    Valider cette adresse
                                </button>
                                <button
                                    onClick={() => setAdresseManuelle(false)}
                                    data-testid="btn-adresse-autocomplete"
                                    className="text-xs text-white/50 underline hover:text-white self-start"
                                >
                                    Revenir à la recherche automatique
                                </button>
                            </>
                        )}
                    </div>
                );
            }

            // ── ACCESSIBILITÉ (4 questions oui/non) ─────────
            case "access_restaurant":
            case "access_parking":
            case "access_auditive":
            case "access_handicap": {
                const config = {
                    access_restaurant: { setter: setIsRestaurant, next: "access_parking", question: "Y a-t-il un parking disponible ?" },
                    access_parking: { setter: setIsParking, next: "access_auditive", question: "L'accessibilité auditive est-elle prévue ?" },
                    access_auditive: { setter: setIsAuditive, next: "access_handicap", question: "Le lieu est-il accessible aux personnes en situation de handicap ?" },
                    access_handicap: { setter: setIsHandicap, next: "billetterie_type", question: "Comment les gens vont-ils réserver ? Lien externe ou billetterie Thrundrz ?" },
                }[step];

                return (
                    <div className="flex gap-3">
                        <button
                            data-testid={`btn-envoyer-R${NUM[step]}-oui`}
                            onClick={() => { config.setter(true); direUser("Oui", { editStep: step }); allerA(config.next, config.question); }}
                            className="flex-1 py-2.5 rounded-xl bg-green-600 text-white font-semibold text-sm hover:bg-green-700 transition"
                        >
                            Oui
                        </button>
                        <button
                            data-testid={`btn-envoyer-R${NUM[step]}-non`}
                            onClick={() => { config.setter(false); direUser("Non", { editStep: step }); allerA(config.next, config.question); }}
                            className="flex-1 py-2.5 rounded-xl bg-gray-500 text-white font-semibold text-sm hover:bg-gray-600 transition"
                        >
                            Non
                        </button>
                    </div>
                );
            }

            // ── TYPE BILLETTERIE ─────────────────────────────
            case "billetterie_type":
                return (
                    <div className="flex gap-3">
                        <button
                            data-testid="btn-envoyer-R19-externe"
                            onClick={() => {
                                setBilletterieType("externe");
                                direUser("Lien externe", { editStep: "billetterie_type" });
                                allerA("billetterie_lien", "Quel est le lien de billetterie ?");
                            }}
                            className="flex-1 py-2.5 rounded-xl bg-gray-500 text-white font-semibold text-sm hover:bg-gray-600 transition"
                        >
                            Lien externe
                        </button>
                        <button
                            data-testid="btn-envoyer-R19-thrundrz"
                            onClick={() => {
                                setBilletterieType("thrundrz");
                                setLienBilletterie("");
                                direUser("Billetterie Thrundrz", { editStep: "billetterie_type" });
                                allerA(
                                    "description",
                                    agitPour
                                        ? "Noté ! (Le créateur devra avoir activé ses paiements Stripe pour vendre des billets.) Décris l'événement (20 caractères min)."
                                        : stripeActif
                                            ? "Parfait, tes paiements sont déjà activés ✅ Décris l'événement (20 caractères min)."
                                            : "Noté ! ⚠️ Pour vendre des billets, tu devras activer tes paiements Stripe (je t'y emmène juste après la publication, ça prend 5 min). Décris l'événement (20 caractères min)."
                                );
                            }}
                            className="flex-1 py-2.5 rounded-xl text-white font-semibold text-sm"
                            style={{ backgroundColor: "#481699" }}
                        >
                            Billetterie Thrundrz
                        </button>
                    </div>
                );

            // ── DESCRIPTION — RichTextEditor (ReactQuill) ────
            case "description": {
                const texteBrut = stripHtml(saisie);
                return (
                    <div className="flex flex-col gap-1">
                        <div data-testid="input-R21">
                            <RichTextEditor value={saisie} onChange={setSaisie} />
                        </div>
                        <div className="flex justify-between items-center">
                            <span className="text-[11px] text-white/30 px-1">
                                {texteBrut.length}/{REGLES.description.maxLength}
                            </span>
                            <button
                                data-testid="btn-envoyer-R21"
                                onClick={() => {
                                    if (!texteBrut) { toast.error("Ce champ est obligatoire."); return; }
                                    if (texteBrut.length < 20 || texteBrut.length > 2000) {
                                        toast.error(REGLES.description.message);
                                        return;
                                    }
                                    setDescription(saisie);
                                    direUser(texteBrut.slice(0, 120) + (texteBrut.length > 120 ? "…" : ""), {
                                        editStep: "description",
                                        editValue: saisie,
                                    });
                                    setSaisie("");
                                    allerA("confirmation", "C'est prêt ! Je publie ton événement ?");
                                }}
                                className="px-5 py-2.5 rounded-xl text-white font-semibold text-sm"
                                style={{ backgroundColor: "#481699" }}
                            >
                                Continuer →
                            </button>
                        </div>
                    </div>
                );
            }

            // ── CONFIRMATION FINALE ──────────────────────────
            case "confirmation":
                return (
                    <button
                        disabled={isSubmitting}
                        data-testid="btn-envoyer-R22"
                        onClick={envoyerEvenement}
                        className="w-full py-3 rounded-xl text-white font-bold disabled:opacity-50 transition"
                        style={{ backgroundColor: "#481699" }}
                    >
                        {isSubmitting ? "Envoi en cours..." : "Envoyer 🚀"}
                    </button>
                );

            default:
                return null;
        }
    };

    // ══════════════════════════════════════════════════════
    //  RENDER GLOBAL
    // ══════════════════════════════════════════════════════

    return (
        <div className="w-full flex justify-center bg-white min-h-screen py-4 px-2 lg:py-8 lg:px-4" data-testid="page-add-event-chat">
            <ToastContainer position="top-right" autoClose={3000} />

            <div className="w-full lg:max-w-[700px] flex flex-col h-[92vh] lg:h-[85vh] bg-color4 border border-white/10 shadow-xl rounded-2xl overflow-hidden">

                <div className="flex items-center gap-3 px-4 py-3 border-b border-white/10 shrink-0">
                    <AvatarBot />
                    <div>
                        <div className="font-bold text-white text-sm">Assistant Thrundrz</div>
                        <div className="text-xs text-green-400 flex items-center gap-1">
                            <span className="w-1.5 h-1.5 rounded-full bg-green-400" /> En ligne
                        </div>
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto px-4 py-4" data-testid="fil-conversation">
                    {messages.map((m, i) => (
                        <Bulle
                            key={i}
                            from={m.from}
                            text={m.text}
                            heure={m.heure}
                            editable={!!m.editStep && !edition}
                            onEdit={() => modifierReponse(i)}
                            testid={m.from === "bot" ? qid(m.step) : rid(m.step)}
                        />
                    ))}
                    {botEcrit && <EnTrainDecrire />}
                    <div ref={scrollRef} />
                </div>

                {step !== "done" && !botEcrit && (
                    <div className="border-t border-white/10 px-4 py-3 shrink-0" data-testid="zone-saisie">
                        {edition && (
                            <div className="flex justify-between items-center mb-2 text-xs text-white/60" data-testid="bandeau-edition">
                                <span>✏️ Modification de ta réponse</span>
                                {!edition.remplace && (
                                    <button
                                        onClick={annulerEdition}
                                        data-testid="btn-annuler-edition"
                                        className="underline hover:text-white"
                                    >
                                        Annuler
                                    </button>
                                )}
                            </div>
                        )}
                        {renderWidget()}
                    </div>
                )}
            </div>
        </div>
    );
};

export default AddEvent2;